/**
 * "Learner" — new player who read the coach / mission tip.
 *
 * Follows teaching, not pro lines:
 * - Plant a Guard Prayer early (Will first — "hold the wall")
 * - Answer Fear with Faith / broad answers
 * - Mend a worn Prayer when the tip says so
 * - Hesitates, sometimes studies a card, weak multi-front / no redeem chains
 *
 * Used as the Easy balance bar: should clear Easy reliably if they follow
 * instructions; should struggle more on Standard/Hard.
 */

import { allLies } from "../game/board/board";
import { LAYER_ORDER, type LayerId } from "../game/board/types";
import { prayerPlaceIntent } from "./prayerPlace";
import type { DragIntent } from "../game/cards/types";
import type { Game } from "../game/Game";
import type { CardInstance, VerseDef } from "../game/cards/types";
import type { BotAction } from "./types";

function layerEnemyCount(game: Game, layer: LayerId): number {
  return game.board[layer].slots.filter((p) => p?.side === "enemy").length;
}

function layerHasPrayer(game: Game, layer: LayerId): boolean {
  return game.board[layer].slots.some((p) => p?.side === "player");
}

function anyPrayer(game: Game): boolean {
  return LAYER_ORDER.some((L) => layerHasPrayer(game, L));
}

function findCard(
  hand: CardInstance[],
  pred: (def: VerseDef) => boolean,
): CardInstance | undefined {
  return hand.find((c) => pred(c.def));
}

function isPlaceable(def: VerseDef): boolean {
  return def.kind === "placeable";
}

function isRestorePrayer(def: VerseDef): boolean {
  return def.kind === "instant" && def.selector === "restorePrayer";
}

/** Simple answers a new player would try first (Faith / Fear tools). */
function isSimpleAnswer(def: VerseDef): boolean {
  if (def.kind !== "instant") return false;
  if (
    def.selector === "convertLie" ||
    def.selector === "empower" ||
    def.selector === "restorePrayer" ||
    def.selector === "gatherPrayers" ||
    def.selector === "self" ||
    def.selector === "slowLayer" ||
    def.selector === "layerChip" ||
    def.selector === "layerSlam"
  ) {
    return false;
  }
  // Soften is advanced "set up Redeem" — learners skip unless nothing else
  if (def.snipeTier === "soften") return false;
  return (
    def.category === "faith" ||
    !!def.strongVs?.includes("fear") ||
    !!def.priorityTypes?.includes("fear") ||
    def.selector === "strongestLie" ||
    def.selector === "innermostLie" ||
    def.selector === "typePriority"
  );
}

function isAnyAnswer(def: VerseDef): boolean {
  if (def.kind !== "instant") return false;
  if (
    def.selector === "empower" ||
    def.selector === "restorePrayer" ||
    def.selector === "gatherPrayers" ||
    def.selector === "self" ||
    def.selector === "convertLie"
  ) {
    return false;
  }
  return true;
}

function placeableTarget(
  game: Game,
  prefer: LayerId,
  def?: VerseDef,
): DragIntent | null {
  return prayerPlaceIntent(game, prefer, def);
}

/** Pick a visible lie — prefer Mind (what N1 teaches), not optimal Will-first. */
function simpleLieIntent(game: Game, prefer?: LayerId): DragIntent | null {
  const lies = allLies(game.board);
  if (!lies.length) return null;
  let pool = lies;
  if (prefer) {
    const f = lies.filter((l) => l.layer === prefer);
    if (f.length) pool = f;
  } else {
    // New players fix what they see on Mind first
    const mind = lies.filter((l) => l.layer === "mind");
    if (mind.length) pool = mind;
  }
  // Not sorted by level / innermost — first they notice
  const t = pool[0]!;
  return {
    layer: t.layer,
    slot: t.slot,
    presenceId: t.presence.instanceId,
  };
}

function damagedPrayer(game: Game): {
  layer: LayerId;
  slot: number;
  presenceId: string;
} | null {
  for (const L of LAYER_ORDER) {
    for (let slot = 0; slot < game.board[L].slots.length; slot++) {
      const p = game.board[L].slots[slot];
      if (p?.side === "player" && p.level > 0 && p.level < p.maxLevel) {
        return { layer: L, slot, presenceId: p.instanceId };
      }
    }
  }
  return null;
}

/** ~15% of decisions: pause and "read" a hand card (Easy reveal coach habit). */
function maybeStudy(hand: CardInstance[]): BotAction | null {
  if (Math.random() > 0.12) return null;
  const card = hand[Math.floor(Math.random() * hand.length)];
  if (!card) return null;
  return {
    type: "reveal",
    instanceId: card.instanceId,
    reason: "study card",
  };
}

/** Occasional freeze — new player thinking what to do. */
function maybeHesitate(): BotAction | null {
  if (Math.random() > 0.08) return null;
  return { type: "wait", reason: "hesitate" };
}

export function learnerDecide(game: Game): BotAction {
  if (game.status !== "playing") return { type: "wait", reason: "not playing" };
  if (game.userPaused) return { type: "wait", reason: "paused" };
  if (game.paused) return { type: "wait", reason: "paused" };
  if (game.isProclaiming()) return { type: "wait", reason: "proclaiming" };

  const hand = game.hand;
  if (hand.length === 0) {
    return { type: "wait", reason: "empty hand" };
  }

  const hesitate = maybeHesitate();
  if (hesitate) return hesitate;

  // Early: read a card sometimes (follows "look at your hand" coach)
  if (allLies(game.board).length === 0 || game.elapsed < 8) {
    const study = maybeStudy(hand);
    if (study) return study;
  }

  const willEnemies = layerEnemyCount(game, "will");
  const willHeld = layerHasPrayer(game, "will");
  const hasPrayer = anyPrayer(game);
  const enemies = allLies(game.board).length;

  // 1. Teaching spine: plant Guard Prayer if none yet (Will first)
  if (!hasPrayer) {
    const prayer = findCard(hand, isPlaceable);
    if (prayer) {
      const intent = placeableTarget(game, "will", prayer.def);
      if (intent) {
        return {
          type: "proclaim",
          instanceId: prayer.instanceId,
          intent,
          reason: "Plant Guard Prayer (coach)",
        };
      }
    }
  }

  // 2. Will open without a keep — emergency plant even if other prayers exist
  if (willEnemies > 0 && !willHeld) {
    const prayer = findCard(hand, isPlaceable);
    if (prayer) {
      const intent = placeableTarget(game, "will", prayer.def);
      if (intent) {
        return {
          type: "proclaim",
          instanceId: prayer.instanceId,
          intent,
          reason: "Prayer on Will (threat)",
        };
      }
    }
  }

  // 3. Mend worn Prayer (1 Thess tip) before piling more answers
  {
    const damaged = damagedPrayer(game);
    const restore = findCard(hand, isRestorePrayer);
    if (damaged && restore) {
      return {
        type: "proclaim",
        instanceId: restore.instanceId,
        intent: {
          layer: damaged.layer,
          slot: damaged.slot,
          presenceId: damaged.presenceId,
        },
        reason: "Mend worn Prayer",
      };
    }
  }

  // 4. Will enemies: answer them (simple aim)
  if (willEnemies > 0) {
    const answer =
      findCard(hand, isSimpleAnswer) ?? findCard(hand, isAnyAnswer);
    if (answer) {
      return {
        type: "proclaim",
        instanceId: answer.instanceId,
        intent: simpleLieIntent(game, "will"),
        reason: "Answer Will threat",
      };
    }
  }

  // 5. Clear visible lies with simple Faith answers
  if (enemies > 0) {
    const simple = findCard(hand, isSimpleAnswer);
    if (simple) {
      return {
        type: "proclaim",
        instanceId: simple.instanceId,
        intent: simpleLieIntent(game),
        reason: "Answer Fear (Faith)",
      };
    }
    const any = findCard(hand, isAnyAnswer);
    if (any) {
      return {
        type: "proclaim",
        instanceId: any.instanceId,
        intent: simpleLieIntent(game),
        reason: "Answer lie (best I have)",
      };
    }
  }

  // 6. Quiet board: expand Prayer if Will empty of keep
  if (!willHeld) {
    const prayer = findCard(hand, isPlaceable);
    if (prayer) {
      const intent = placeableTarget(game, "will", prayer.def);
      if (intent) {
        return {
          type: "proclaim",
          instanceId: prayer.instanceId,
          intent,
          reason: "Abide Will",
        };
      }
    }
  }

  // 7. Still nothing useful — rare random placeable expand to Mind
  if (enemies === 0) {
    const prayer = findCard(hand, isPlaceable);
    if (prayer && willHeld) {
      const intent = placeableTarget(game, "mind", prayer.def);
      if (intent && intent.layer !== "will") {
        return {
          type: "proclaim",
          instanceId: prayer.instanceId,
          intent,
          reason: "Expand Prayer",
        };
      }
    }
  }

  return { type: "wait", reason: "no legal play" };
}
